import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Target, Plus, Trash2, Users, MousePointerClick, X } from "lucide-react";
import { sr } from "../../lib/sr";

const PSTATE = {
  not_connected: { l: "Not connected", c: "#64748B", bg: "#F1F5F9" },
  blocked_test_mode: { l: "Test Mode on", c: "#92400E", bg: "#FEF3C7" },
  activating: { l: "Activating", c: "#1D4ED8", bg: "#DBEAFE" },
  active: { l: "Active", c: "#166534", bg: "#DCFCE7" },
  error: { l: "Error", c: "#B91C1C", bg: "#FEE2E2" },
  action_required: { l: "Action needed", c: "#B91C1C", bg: "#FEE2E2" },
};
const PROVIDERS = [{ k: "meta", l: "Meta" }, { k: "ad_platform", l: "Ad Platform" }];

function ProviderCell({ aud, prov, provStatus, onActivate }) {
  const p = aud.providers?.[prov.k];
  const [busy, setBusy] = useState(false);
  const st = p?.status ? (PSTATE[p.status] || PSTATE.not_connected) : null;
  const go = async () => { setBusy(true); try { await onActivate(aud.id, prov.k); } finally { setBusy(false); } };
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: "var(--navy)", width: 78 }}>{prov.l}</span>
      {st ? <span data-testid={`aud-${aud.id}-${prov.k}-status`} style={{ fontSize: 11, fontWeight: 700, borderRadius: 999, padding: "2px 9px", background: st.bg, color: st.c }}>{st.l}</span>
        : <span className="sr-muted" style={{ fontSize: 11 }}>—</span>}
      <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={go} disabled={busy} data-testid={`aud-${aud.id}-activate-${prov.k}`} style={{ marginLeft: "auto" }}>
        {busy ? <Loader2 size={12} className="sr-spin" /> : (p?.status === "active" ? "Re-sync" : "Activate")}
      </button>
    </div>
  );
}

function AudienceCard({ aud, onActivate, onDelete }) {
  const KIND = aud.kind === "visitors" ? { l: "Bring visitors back", icon: MousePointerClick } : { l: "Re-engage customers", icon: Users };
  return (
    <div className="sr-card" data-testid={`audience-${aud.id}`}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <KIND.icon size={16} style={{ color: "var(--blue)" }} />
        <strong style={{ flex: 1, color: "var(--navy)", fontSize: 15 }}>{aud.name}</strong>
        <button className="sr-btn sr-btn-ghost sr-btn-sm" onClick={() => onDelete(aud.id)} data-testid={`audience-delete-${aud.id}`}><Trash2 size={13} /></button>
      </div>
      <div className="sr-muted" style={{ fontSize: 12, margin: "4px 0 2px" }}>{KIND.l}{aud.kind === "visitors" ? ` · last ${aud.lookback_days} days` : ` · ${aud.member_count} customers`}</div>
      <div style={{ fontSize: 13, color: "var(--navy)", fontWeight: 700 }}>~{Number(aud.size_estimate || 0).toLocaleString()} <span className="sr-muted" style={{ fontWeight: 400, fontSize: 11 }}>est. reach (planning)</span></div>
      <div style={{ display: "grid", gap: 8, marginTop: 12, borderTop: "1px solid var(--border)", paddingTop: 10 }}>
        {PROVIDERS.map((prov) => <ProviderCell key={prov.k} aud={aud} prov={prov} onActivate={onActivate} />)}
      </div>
    </div>
  );
}

function CreateForm({ onCreated }) {
  const [kind, setKind] = useState("visitors");
  const [name, setName] = useState("");
  const [lookback, setLookback] = useState(30);
  const [members, setMembers] = useState("");
  const [busy, setBusy] = useState(false);
  const create = async () => {
    if (!name.trim()) { toast.error("Name your audience"); return; }
    setBusy(true);
    try {
      const body = { name, kind };
      if (kind === "visitors") body.lookback_days = Number(lookback) || 30;
      else body.members = members.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
      await sr.createAudience(body);
      toast.success("Audience created"); setName(""); setMembers(""); onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create audience"); }
    finally { setBusy(false); }
  };
  return (
    <div className="sr-card" style={{ marginBottom: 16 }} data-testid="audience-create-form">
      <strong style={{ color: "var(--navy)" }}>Create a retargeting audience</strong>
      <div style={{ display: "flex", gap: 8, margin: "10px 0" }}>
        {[{ k: "visitors", l: "Bring visitors back" }, { k: "customers", l: "Re-engage customers" }].map((o) => (
          <button key={o.k} type="button" onClick={() => setKind(o.k)} data-testid={`audience-kind-${o.k}`}
            style={{ border: "1px solid " + (kind === o.k ? "var(--blue)" : "var(--border)"), background: kind === o.k ? "var(--blue)" : "#fff", color: kind === o.k ? "#fff" : "var(--navy)", borderRadius: 999, padding: "7px 14px", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
            {o.l}
          </button>
        ))}
      </div>
      <div className="sr-field"><label className="sr-label">Audience name</label>
        <input className="sr-input" value={name} onChange={(e) => setName(e.target.value)} placeholder={kind === "visitors" ? "Website visitors – 30 days" : "Past customers"} data-testid="audience-name" /></div>
      {kind === "visitors" ? (
        <div className="sr-field"><label className="sr-label">Look back (days)</label>
          <input className="sr-input" type="number" min="1" max="180" value={lookback} onChange={(e) => setLookback(e.target.value)} data-testid="audience-lookback" />
          <p className="sr-muted" style={{ fontSize: 11.5, marginTop: 4 }}>Built from your site pixel — people who visited in this window.</p></div>
      ) : (
        <div className="sr-field"><label className="sr-label">Customer emails</label>
          <textarea className="sr-input" rows={4} value={members} onChange={(e) => setMembers(e.target.value)} placeholder="one per line or comma-separated" data-testid="audience-members" />
          <p className="sr-muted" style={{ fontSize: 11.5, marginTop: 4 }}>Emails are hashed (SHA-256) before upload — raw addresses are never stored.</p></div>
      )}
      <button className="sr-btn sr-btn-primary" onClick={create} disabled={busy} data-testid="audience-create-btn">{busy ? <Loader2 size={14} className="sr-spin" /> : <><Plus size={14} /> Create audience</>}</button>
    </div>
  );
}

export default function Retargeting() {
  const [audiences, setAudiences] = useState(null);
  const [providers, setProviders] = useState(null);
  const load = () => sr.retargetingAudiences().then((r) => { setAudiences(r.audiences || []); setProviders(r.providers); }).catch(() => setAudiences([]));
  useEffect(() => { load(); }, []);

  const activate = async (id, provider) => {
    try { const r = await sr.activateAudience(id, provider); const st = r?.result?.status; if (st === "active") toast.success(`Activated on ${provider === "meta" ? "Meta" : "the Ad Platform"}`); else toast.message(r?.result?.error || "Updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Activation failed"); }
  };
  const del = async (id) => { try { await sr.deleteAudience(id); toast.success("Removed"); load(); } catch { toast.error("Could not remove"); } };

  return (
    <div style={{ maxWidth: 820 }} data-testid="retargeting-page">
      <h1 style={{ fontSize: 24, color: "var(--navy)", margin: "0 0 4px" }} data-testid="retargeting-heading"><Target size={22} style={{ verticalAlign: "-3px" }} /> Retargeting Audiences</h1>
      <p className="sr-muted" style={{ fontSize: 13, marginBottom: 16 }}>Bring past visitors back and re-engage customers — activated on Meta and the Ad Platform with real status.</p>

      {providers && (
        <div className="sr-card" style={{ marginBottom: 16, display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }} data-testid="retargeting-providers">
          <span style={{ fontSize: 12.5, color: "var(--navy)" }}><b>Meta:</b> {providers.meta?.connected ? "Connected" : "Not connected"}</span>
          <span style={{ fontSize: 12.5, color: "var(--navy)" }}><b>Ad Platform:</b> {providers.ad_platform?.connected ? "Connected" : "Not connected"}</span>
          {providers.test_mode && <span style={{ fontSize: 11.5, fontWeight: 700, borderRadius: 999, padding: "3px 10px", background: "#FEF3C7", color: "#92400E" }} data-testid="retargeting-testmode">Test Mode on — real pushes paused</span>}
        </div>
      )}

      <CreateForm onCreated={load} />

      {audiences === null ? <div className="sr-empty"><Loader2 className="sr-spin" /></div>
        : audiences.length === 0 ? <div className="sr-empty" data-testid="retargeting-empty">No audiences yet. Create one above to start bringing people back.</div>
        : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: 14 }}>
            {audiences.map((a) => <AudienceCard key={a.id} aud={a} onActivate={activate} onDelete={del} />)}
          </div>
        )}
    </div>
  );
}
